import { useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { BaseClickableIcon } from "../../../components/Icon/BaseClickableIcon.tsx";
import { IMAGE_RESOURCE_TYPE, VIDEO_RESOURCE_TYPE } from "../constants.ts";
import { usePurchaseAndDownload } from "../hooks/usePurchaseAndDownload.ts";
import type { Asset } from "../types.ts";
import { ContentDownloadDialog } from "./ContentDownloadDialog.tsx";

export const ContentDownloadButton = ({
  asset,
  variant = "full",
}: {
  asset?: Asset;
  variant?: "full" | "icon" | "icon-inverse";
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    mutate: purchaseAndDownload,
    isLoading: isLoadingPurchaseAndDownload,
  } = usePurchaseAndDownload({
    assetUuids: asset ? [asset.uuid] : [],
    contentUuids: asset ? [asset.content.uuid] : [],
  });

  const onClick = () => {
    if (!asset) return;
    if (asset.content.purchased_at === null) {
      setIsDialogOpen(true);
    } else {
      purchaseAndDownload();
    }
  };
  const isDisabled =
    isLoadingPurchaseAndDownload || asset?.content.status !== "SUCCEEDED";

  let buttonText = "Download content";
  if (asset?.content.resource_type) {
    switch (asset.content.resource_type) {
      case IMAGE_RESOURCE_TYPE:
        buttonText = "Download image";
        break;
      case VIDEO_RESOURCE_TYPE:
        buttonText = "Download video";
        break;
    }
  }

  return (
    <>
      {asset && (
        <ContentDownloadDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          assetUuids={[asset.uuid]}
        />
      )}
      {variant === "icon" ? (
        <BaseClickableIcon
          name="Download"
          size="sm"
          className="z-10 tile-action"
          tooltip={{ side: "top", content: "Download" }}
          onClick={onClick}
          isDisabled={isDisabled}
          isLoading={isLoadingPurchaseAndDownload}
        />
      ) : variant === "icon-inverse" ? (
        <BaseClickableIcon
          name="Download"
          size="sm"
          iconClassName="stroke-inverse-rest"
          className="h-800 w-800 rounded-100 hover:bg-surface-inverse-hover active:bg-surface-inverse-active"
          tooltip={{ side: "top", content: "Download" }}
          onClick={onClick}
          isDisabled={isDisabled}
          isLoading={isLoadingPurchaseAndDownload}
        />
      ) : (
        <Button
          onClick={onClick}
          className="flex-row-center gap-200"
          iconName="Download"
          size="md"
          disabled={isDisabled}
          loading={isLoadingPurchaseAndDownload}
        >
          {buttonText}
        </Button>
      )}
    </>
  );
};
